// Google's Search suggestions chip for a Gemini-grounded web answer (busy-ai-srv#24).
//
// When the Gemini engine answers a web search, ai-srv streams a `searchSuggestions` event carrying
// Google's `renderedContent` — a self-contained block of HTML + CSS (the Google logo and a row of
// search chips). Google's grounding terms REQUIRE it to be shown with the answer, exactly as given,
// and forbid editing the answer around it. So the chip is drawn in a sandboxed iframe: Google's CSS
// cannot leak into the app, the app's Tailwind cannot restyle it, and it runs no script.
//
// Pure, so the rules are tested without mounting the workspace.

/**
 * THE ONE SWITCH for drawing Google's Search suggestions chip (busy-my-web#682). Pouria's final
 * web-search design (Figma 40015125:58088) draws NO "Search suggestions" line under an answer; the
 * compliance question it raises is open with him, so the chip is hidden — not deleted. Every place
 * that draws it (Research's workspace, Home's web-answer block) reads this flag, and the stream and
 * the store still carry the HTML, so turning it back on is this one line.
 */
export const SHOW_SEARCH_SUGGESTIONS = false

/**
 * The iframe's sandbox: no scripts, no same-origin, no top navigation — only the popup a chip's link
 * opens, which then escapes the sandbox so Google's results page works normally in its new tab.
 */
export const SEARCH_SUGGESTIONS_SANDBOX = 'allow-popups allow-popups-to-escape-sandbox'

/**
 * Fixed height of the chip row. The sandbox has no scripts, so the frame cannot report its own
 * height; Google's block is one non-wrapping, horizontally scrolling row (8px padding + a 32px chip
 * + 8px padding), and 56px fits it with room for its 1px outline.
 */
export const SEARCH_SUGGESTIONS_HEIGHT = 56

/**
 * The chip's HTML from whatever carried it, or '' when there is none. The stream's event and the
 * BFF's one-shot body send `{ html }`; a bare string is read as the HTML itself, so a payload that
 * flattens the object still draws.
 *
 * @param {string|{html?: string, Html?: string}|null|undefined} value
 * @returns {string}
 */
export function searchSuggestionsHtml (value) {
  if (typeof value === 'string') return value.trim() ? value : ''
  if (value && typeof value === 'object') {
    const html = typeof value.html === 'string' ? value.html : (typeof value.Html === 'string' ? value.Html : '')
    return html.trim() ? html : ''
  }
  return ''
}

/**
 * The iframe's document: Google's HTML untouched inside a bare page. `<base target="_blank">` makes
 * every chip open in a new tab (the frame may not navigate itself or the app), the page margin is
 * zeroed so the row sits flush, and the background stays transparent so the block reads the same on
 * any surface — Google's own CSS paints the chip container.
 *
 * @param {string} html Google's renderedContent, as received.
 * @returns {string}
 */
export function searchSuggestionsSrcdoc (html) {
  return '<!doctype html><html><head><meta charset="utf-8"><base target="_blank">' +
    '<style>html,body{margin:0;padding:0;background:transparent;overflow:hidden}</style>' +
    `</head><body>${html || ''}</body></html>`
}

/**
 * True when an answer payload says Gemini wrote it, grounded on Google Search. Such an answer is
 * shown exactly as written: no leaked-section cut, no filler lines dropped (busy-ai-srv#24).
 *
 * @param {{grounded?: boolean, engine?: string}|null|undefined} payload
 * @returns {boolean}
 */
export function isGroundedAnswer (payload) {
  if (!payload || typeof payload !== 'object') return false
  return payload.grounded === true || String(payload.engine || '').toLowerCase() === 'gemini'
}
